import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import { projectsAPI, sitesAPI, camerasAPI, zonesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  Video,
  MapPin,
  Edit,
  Trash2,
  Pencil,
  Building2,
  Tag,
  X,
  Target,
  Filter,
  Camera as CameraIcon,
  FolderOpen,
  Upload,
  Image as ImageIcon,
  Network,
  Search,
  FileDown
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';

export default function CameraHub() {
  const navigate = useNavigate();
  const { selectedProject } = useProject();

  // Data states
  const [sites, setSites] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [zoneCounts, setZoneCounts] = useState({});

  // Filter states
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCameras, setSelectedCameras] = useState([]);

  // Modal states
  const [showSiteDialog, setShowSiteDialog] = useState(false);
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importText, setImportText] = useState('');

  // Form states
  const [siteForm, setSiteForm] = useState({ name: '', address: '', city: '', state: '', country: '' });
  const [cameraForm, setCameraForm] = useState({
    name: '',
    camera_id: '',
    site_id: '',
    ip_address: '',
    location: '',
    description: '',
    rtsp_url: '',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Editing states
  const [editingSite, setEditingSite] = useState(null);
  const [editingCamera, setEditingCamera] = useState(null);

  // Load sites when project changes
  useEffect(() => {
    if (selectedProject?.id) {
      loadSites(selectedProject.id);
      setSelectedSiteId(''); // Reset site selection
    } else {
      setSites([]);
      setSelectedSiteId('');
    }
  }, [selectedProject]);

  // Load cameras when project or site changes
  useEffect(() => {
    if (selectedProject?.id) {
      loadCameras();
    } else {
      setCameras([]);
    }
  }, [selectedProject, selectedSiteId]);

  const loadSites = async (projectId) => {
    try {
      const result = await sitesAPI.getAll(projectId, { limit: 1000 });
      setSites(result.sites || []);
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  };

  const loadCameras = async () => {
    try {
      setLoading(true);
      let result;

      if (selectedSiteId && selectedSiteId !== '__all__') {
        // Load cameras for specific site
        result = await camerasAPI.getAll(selectedSiteId, { limit: 1000 });
      } else if (selectedProject?.id) {
        // Load all cameras for the project by project_id
        result = await camerasAPI.getAllByProject(selectedProject.id, { limit: 1000 });
      } else {
        result = { cameras: [] };
      }

      const cameraList = result.cameras || [];

      // Parse tags from JSON strings
      const camerasWithParsedTags = cameraList.map(camera => ({
        ...camera,
        tags: camera.tags ? (typeof camera.tags === 'string' ? JSON.parse(camera.tags) : camera.tags) : []
      }));

      setCameras(camerasWithParsedTags);

      // Extract all unique tags
      const tagsSet = new Set();
      camerasWithParsedTags.forEach(camera => {
        camera.tags.forEach(tag => tagsSet.add(tag));
      });
      setAllTags(Array.from(tagsSet).sort());

      // Load zone counts for each camera
      const counts = {};
      await Promise.all(
        cameraList.map(async (camera) => {
          try {
            const zonesResult = await zonesAPI.getAll(camera.id, { limit: 1 });
            counts[camera.id] = zonesResult.pagination?.total || zonesResult.zones?.length || 0;
          } catch (err) {
            counts[camera.id] = 0;
          }
        })
      );
      setZoneCounts(counts);
    } catch (err) {
      setError('Failed to load cameras');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Site CRUD
  const handleCreateSite = async (e) => {
    e.preventDefault();
    try {
      await sitesAPI.create({
        ...siteForm,
        project_id: selectedProject?.id,
      });
      setShowSiteDialog(false);
      setSiteForm({ name: '', address: '', city: '', state: '', country: '' });
      loadSites(selectedProject.id);
    } catch (err) {
      setError('Failed to create site');
    }
  };

  const handleUpdateSite = async (e) => {
    e.preventDefault();
    try {
      await sitesAPI.update(editingSite.id, siteForm);
      setShowSiteDialog(false);
      setEditingSite(null);
      setSiteForm({ name: '', address: '', city: '', state: '', country: '' });
      loadSites(selectedProject.id);
    } catch (err) {
      setError('Failed to update site');
    }
  };

  const handleDeleteSite = async (id) => {
    if (!window.confirm('Delete this site? This will delete all cameras and zones.')) return;
    try {
      await sitesAPI.delete(id);
      if (selectedSiteId === id) {
        setSelectedSiteId('');
      }
      loadSites(selectedProject.id);
      loadCameras();
    } catch (err) {
      setError('Failed to delete site');
    }
  };

  // Camera CRUD
  const handleCreateCamera = async (e) => {
    e.preventDefault();
    try {
      const result = await camerasAPI.create({
        ...cameraForm,
        project_id: selectedProject?.id,
        site_id: cameraForm.site_id || null,
      });

      // Upload image if provided
      if (imageFile && result.camera?.id) {
        await camerasAPI.updateImage(result.camera.id, imageFile);
      }

      setShowCameraDialog(false);
      setCameraForm({ name: '', camera_id: '', site_id: '', ip_address: '', location: '', description: '', rtsp_url: '', tags: [] });
      setTagInput('');
      setImageFile(null);
      setImagePreview(null);
      loadCameras();
    } catch (err) {
      setError('Failed to create camera');
    }
  };

  const handleUpdateCamera = async (e) => {
    e.preventDefault();
    try {
      // Prepare camera data with proper null handling for site_id
      const updateData = {
        ...cameraForm,
        site_id: cameraForm.site_id === '' || cameraForm.site_id === '__none__' ? null : cameraForm.site_id
      };

      await camerasAPI.update(editingCamera.id, updateData);

      // Upload new image if provided
      if (imageFile) {
        await camerasAPI.updateImage(editingCamera.id, imageFile);
      }

      setShowCameraDialog(false);
      setEditingCamera(null);
      setCameraForm({ name: '', camera_id: '', site_id: '', ip_address: '', location: '', description: '', rtsp_url: '', tags: [] });
      setTagInput('');
      setImageFile(null);
      setImagePreview(null);
      loadCameras();
    } catch (err) {
      setError('Failed to update camera');
    }
  };

  const handleDeleteCamera = async (id) => {
    if (!window.confirm('Delete this camera? This will delete all zones.')) return;
    try {
      await camerasAPI.delete(id);
      loadCameras();
    } catch (err) {
      setError('Failed to delete camera');
    }
  };

  const handleImportCameras = async () => {
    try {
      setError('');
      const lines = importText.trim().split('\n').filter(line => line.trim());

      if (lines.length === 0) {
        setError('Please enter camera data to import');
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const line of lines) {
        try {
          // Expected format: name,camera_id,rtsp_url[,ip_address][,location]
          const parts = line.split(',').map(p => p.trim());

          if (parts.length < 3) {
            errors.push(`Invalid line: ${line} (need at least name, camera_id, rtsp_url)`);
            errorCount++;
            continue;
          }

          const [name, camera_id, rtsp_url, ip_address, location] = parts;

          // Extract IP from RTSP if not provided
          const finalIp = ip_address || extractIpFromRtsp(rtsp_url);

          const result = await camerasAPI.create({
            name,
            camera_id,
            rtsp_url,
            ip_address: finalIp,
            location: location || '',
            site_id: (selectedSiteId && selectedSiteId !== '__all__') ? selectedSiteId : null,
            project_id: selectedProject?.id,
            tags: [],
          });

          successCount++;
        } catch (err) {
          errors.push(`Failed to import: ${line} - ${err.message}`);
          errorCount++;
        }
      }

      setShowImportDialog(false);
      setImportText('');

      if (successCount > 0) {
        loadCameras();
      }

      if (errorCount > 0) {
        setError(`Imported ${successCount} cameras, ${errorCount} failed. ${errors.slice(0, 3).join('; ')}`);
      } else {
        setError('');
      }
    } catch (err) {
      setError('Import failed: ' + err.message);
    }
  };

  const handleDrawZones = (camera) => {
    navigate(`/zones/editor?cameraId=${camera.id}`);
  };

  const handleExportZones = async (camera) => {
    try {
      const result = await zonesAPI.getAll(camera.id);
      if (!result.zones || result.zones.length === 0) {
        alert('No zones to export for this camera');
        return;
      }

      // Get image dimensions from camera
      const imgWidth = camera.width || 1920;
      const imgHeight = camera.height || 1080;

      // Convert zones to point arrays
      const masterPoints = [];
      const masterZoneTypes = [];

      result.zones.forEach(zone => {
        const zoneType = zone.zone_type;
        masterZoneTypes.push(zoneType);

        if (zoneType === 'line_crossing') {
          // Line crossing: convert to flat array [x1d, y1d, x2d, y2d, x1c, y1c, x2c, y2c]
          const flatPoints = zone.points.flatMap(p => [p.x * imgWidth, p.y * imgHeight]);
          masterPoints.push(flatPoints);
        } else if (zoneType === 'direction_detection') {
          // Direction: convert to flat array [x1, y1, x2, y2]
          const flatPoints = zone.points.flatMap(p => [p.x * imgWidth, p.y * imgHeight]);
          masterPoints.push(flatPoints);
        } else {
          // ROI Filtering, Overcrowding: convert to array of [x, y] pairs
          const pointPairs = zone.points.map(p => [p.x * imgWidth, p.y * imgHeight]);
          masterPoints.push(pointPairs);
        }
      });

      // Dynamic import to avoid bundling issues
      const { exportDeepStreamConfig } = await import('../utils/coordinateUtils');
      exportDeepStreamConfig(
        masterPoints,
        masterZoneTypes,
        imgWidth,
        imgHeight,
        camera.name,
        camera.camera_id,
        `${camera.name}_zones.txt`
      );
    } catch (err) {
      console.error('Failed to export zones:', err);
      alert('Failed to export zones');
    }
  };

  const handleBulkExportZones = async () => {
    if (selectedCameras.length === 0) {
      alert('Please select cameras to export');
      return;
    }

    try {
      setLoading(true);
      let combinedConfig = `# DeepStream Configuration
# Generated from PolygonZone Zone Editor
# Bulk Export - ${selectedCameras.length} cameras

################################################################################
# SOURCES
################################################################################

`;

      // First, export all sources
      selectedCameras.forEach((cameraId, index) => {
        const camera = cameras.find(c => c.id === cameraId);
        if (!camera) return;

        combinedConfig += `[source${index}]\n`;
        combinedConfig += `enable=1\n`;
        combinedConfig += `type=4\n`;
        combinedConfig += `uri=${camera.rtsp_url || ''}\n`;
        combinedConfig += `capture-stream=\n`;
        combinedConfig += `gpu-id=0\n`;
        combinedConfig += `cudadec-memtype=0\n`;
        combinedConfig += `camera-name=${camera.name}\n`;
        combinedConfig += `camera-id=${camera.camera_id}\n\n`;
      });

      combinedConfig += `################################################################################
# ANALYTICS ZONES
################################################################################
`;

      // Then, export zones for each camera
      for (const cameraId of selectedCameras) {
        const camera = cameras.find(c => c.id === cameraId);
        if (!camera) continue;

        const result = await zonesAPI.getAll(camera.id);
        if (!result.zones || result.zones.length === 0) continue;

        const imgWidth = camera.width || 1920;
        const imgHeight = camera.height || 1080;

        combinedConfig += `\n# ========================================\n`;
        combinedConfig += `# Camera: ${camera.name} (Camera ID: ${camera.camera_id})\n`;
        combinedConfig += `# ========================================\n\n`;

        // Group zones by type
        const lineCrossingZones = [];
        const roiFilteringZones = [];
        const overcrowdingZones = [];
        const directionZones = [];

        result.zones.forEach(zone => {
          const zoneType = zone.zone_type;
          let points;

          if (zoneType === 'line_crossing') {
            points = zone.points.flatMap(p => [p.x * imgWidth, p.y * imgHeight]);
            lineCrossingZones.push({ points });
          } else if (zoneType === 'direction_detection') {
            points = zone.points.flatMap(p => [p.x * imgWidth, p.y * imgHeight]);
            directionZones.push({ points });
          } else if (zoneType === 'roi_filtering') {
            points = zone.points.map(p => [p.x * imgWidth, p.y * imgHeight]);
            roiFilteringZones.push({ points });
          } else if (zoneType === 'overcrowding') {
            points = zone.points.map(p => [p.x * imgWidth, p.y * imgHeight]);
            overcrowdingZones.push({ points });
          }
        });

        // Export line crossing zones
        if (lineCrossingZones.length > 0) {
          combinedConfig += `[line-crossing-stream-${camera.camera_id}]\n`;
          combinedConfig += `enable=1\n`;
          combinedConfig += `class-id=-1\n`;
          combinedConfig += `extended=1\n`;
          combinedConfig += `mode=balanced\n`;

          lineCrossingZones.forEach((zone, zoneIndex) => {
            const { points } = zone;
            if (points.length === 8) {
              const coordString = `${Math.round(points[0])};${Math.round(points[1])};${Math.round(points[2])};${Math.round(points[3])};${Math.round(points[4])};${Math.round(points[5])};${Math.round(points[6])};${Math.round(points[7])}`;
              combinedConfig += `line-crossing-tripwire-${zoneIndex + 1}-${camera.name}=${coordString}\n`;
            }
          });
          combinedConfig += `\n`;
        }

        // Export ROI filtering zones
        if (roiFilteringZones.length > 0) {
          combinedConfig += `[roi-filtering-stream-${camera.camera_id}]\n`;
          combinedConfig += `enable=1\n`;
          combinedConfig += `inverse-roi=0\n`;
          combinedConfig += `class-id=-1\n`;

          roiFilteringZones.forEach((zone, zoneIndex) => {
            const { points } = zone;
            const coordString = points.map((p) => `${Math.round(p[0])};${Math.round(p[1])}`).join(";");
            combinedConfig += `roi-RF-zone-${zoneIndex + 1}-${camera.name}=${coordString}\n`;
          });
          combinedConfig += `\n`;
        }

        // Export overcrowding zones
        if (overcrowdingZones.length > 0) {
          combinedConfig += `[overcrowding-stream-${camera.camera_id}]\n`;
          combinedConfig += `enable=1\n`;
          combinedConfig += `object-threshold=5\n`;
          combinedConfig += `class-id=-1\n`;

          overcrowdingZones.forEach((zone, zoneIndex) => {
            const { points } = zone;
            const coordString = points.map((p) => `${Math.round(p[0])};${Math.round(p[1])}`).join(";");
            combinedConfig += `roi-OC-zone-${zoneIndex + 1}-${camera.name}=${coordString}\n`;
          });
          combinedConfig += `\n`;
        }

        // Export direction detection zones
        if (directionZones.length > 0) {
          combinedConfig += `[direction-detection-stream-${camera.camera_id}]\n`;
          combinedConfig += `enable=1\n`;
          combinedConfig += `class-id=0\n`;

          directionZones.forEach((zone, zoneIndex) => {
            const { points } = zone;
            if (points.length >= 2) {
              const directionCoords = `${Math.round(points[0])};${Math.round(points[1])};${Math.round(points[points.length - 2])};${Math.round(points[points.length - 1])}`;
              combinedConfig += `direction-zone-${zoneIndex + 1}-${camera.name}=${directionCoords}\n`;
            }
          });
          combinedConfig += `\n`;
        }
      }

      // Download combined config
      const blob = new Blob([combinedConfig], { type: "text/plain" });
      const link = document.createElement("a");
      link.download = `bulk_export_${selectedCameras.length}_cameras.txt`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);

      setSelectedCameras([]);
    } catch (err) {
      console.error('Failed to bulk export zones:', err);
      alert('Failed to bulk export zones');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCameras.length === 0) return;
    if (!window.confirm(`Delete ${selectedCameras.length} camera${selectedCameras.length > 1 ? 's' : ''}? This will delete all their zones too.`)) return;
    try {
      setLoading(true);
      for (const cameraId of selectedCameras) {
        await camerasAPI.delete(cameraId);
      }
      setSelectedCameras([]);
      loadCameras();
    } catch (err) {
      setError('Failed to delete some cameras');
    } finally {
      setLoading(false);
    }
  };

  const openEditSite = (site) => {
    setEditingSite(site);
    setSiteForm({
      name: site.name,
      address: site.address || '',
      city: site.city || '',
      state: site.state || '',
      country: site.country || '',
    });
    setShowSiteDialog(true);
  };

  const openEditCamera = (camera) => {
    setEditingCamera(camera);
    setCameraForm({
      name: camera.name,
      camera_id: camera.camera_id,
      site_id: camera.site_id || '',
      ip_address: camera.ip_address || '',
      location: camera.location || '',
      description: camera.description || '',
      rtsp_url: camera.rtsp_url || '',
      tags: camera.tags || [],
    });
    setImagePreview(camera.image_url || null);
    setShowCameraDialog(true);
  };

  const extractIpFromRtsp = (rtspUrl) => {
    if (!rtspUrl) return '';

    try {
      // Parse RTSP URL to extract IP/hostname
      // Format: rtsp://[user:pass@]ip:port/path or rtsp://ip/path
      const url = new URL(rtspUrl);
      const hostname = url.hostname;

      // Check if it's an IP address (basic validation)
      if (hostname && /^[\d.]+$/.test(hostname)) {
        return hostname;
      }

      return hostname || '';
    } catch (e) {
      // If URL parsing fails, try regex
      const ipMatch = rtspUrl.match(/rtsp:\/\/(?:[^:@]+:[^@]+@)?([\d.]+)(?::|\/)/);
      return ipMatch ? ipMatch[1] : '';
    }
  };

  const handleRtspUrlChange = (value) => {
    setCameraForm({ ...cameraForm, rtsp_url: value });

    // Auto-extract IP if not manually set
    if (!cameraForm.ip_address || cameraForm.ip_address === extractIpFromRtsp(cameraForm.rtsp_url)) {
      const extractedIp = extractIpFromRtsp(value);
      if (extractedIp) {
        setCameraForm({ ...cameraForm, rtsp_url: value, ip_address: extractedIp });
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    const tag = tagInput.trim().toLowerCase();
    if (tag && !cameraForm.tags.includes(tag)) {
      setCameraForm({ ...cameraForm, tags: [...cameraForm.tags, tag] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setCameraForm({
      ...cameraForm,
      tags: cameraForm.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const toggleTagFilter = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const getSiteNameById = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || 'Unknown Site';
  };

  // Filter cameras by selected tags and search query
  const filteredCameras = cameras.filter(camera => {
    // Tag filter
    const tagMatch = selectedTags.length === 0 || selectedTags.every(tag => camera.tags.includes(tag));

    // Search filter (name, camera_id, ip_address)
    const query = searchQuery.toLowerCase();
    const searchMatch = !query ||
      camera.name.toLowerCase().includes(query) ||
      camera.camera_id.toLowerCase().includes(query) ||
      (camera.ip_address && camera.ip_address.toLowerCase().includes(query));

    return tagMatch && searchMatch;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Compact Toolbar */}
      <div className="border-b bg-card sticky top-[57px] z-40">
        <div className="flex items-center justify-between px-4 py-2 gap-4">
          {/* Left: Filters */}
          <div className="flex items-center gap-3">
            {/* Site Filter */}
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <Select
                value={selectedSiteId}
                onValueChange={setSelectedSiteId}
                disabled={!selectedProject}
              >
                <SelectTrigger className="w-[180px] h-8">
                  <SelectValue placeholder={selectedProject ? "All sites" : "Select project first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Sites</SelectItem>
                  {sites.filter(site => site.id && site.id !== '').map((site) => (
                    <SelectItem key={site.id} value={site.id.toString()}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                disabled={!selectedProject}
                onClick={() => {
                  setEditingSite(null);
                  setSiteForm({ name: '', address: '', city: '', state: '', country: '' });
                  setShowSiteDialog(true);
                }}
                title="Add Site"
              >
                <Plus className="h-4 w-4" />
              </Button>
              {selectedSiteId && selectedSiteId !== '__all__' && (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={() => {
                      const site = sites.find(s => s.id === selectedSiteId);
                      if (site) openEditSite(site);
                    }}
                    title="Edit Site"
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-destructive"
                    onClick={() => handleDeleteSite(selectedSiteId)}
                    title="Delete Site"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </>
              )}
            </div>

            {/* Tag Filter */}
            {allTags.length > 0 && (
              <>
                <div className="h-6 w-px bg-border" />
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <div className="flex flex-wrap gap-1">
                    {allTags.map((tag) => (
                      <Badge
                        key={tag}
                        variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                        className="cursor-pointer hover:bg-primary/80 transition-colors h-6 text-xs"
                        onClick={() => toggleTagFilter(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                    {selectedTags.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedTags([])}
                        className="h-6 px-2 text-xs"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right: Search & Actions */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by name, ID, or IP..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 w-[250px]"
              />
            </div>
            {filteredCameras.length > 0 && (
              <label className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary cursor-pointer"
                  checked={filteredCameras.length > 0 && filteredCameras.every(c => selectedCameras.includes(c.id))}
                  ref={el => {
                    if (el) el.indeterminate = selectedCameras.length > 0 && !filteredCameras.every(c => selectedCameras.includes(c.id));
                  }}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const ids = filteredCameras.map(c => c.id);
                      setSelectedCameras(prev => [...new Set([...prev, ...ids])]);
                    } else {
                      const filteredIds = new Set(filteredCameras.map(c => c.id));
                      setSelectedCameras(prev => prev.filter(id => !filteredIds.has(id)));
                    }
                  }}
                />
                <span className="text-sm text-muted-foreground">
                  {selectedCameras.length > 0
                    ? `${selectedCameras.length} of ${filteredCameras.length} selected`
                    : `${filteredCameras.length} ${filteredCameras.length === 1 ? 'camera' : 'cameras'}`}
                </span>
              </label>
            )}
            {selectedCameras.length > 0 && (
              <>
                <Button
                  size="sm"
                  variant="default"
                  onClick={handleBulkExportZones}
                  className="gap-2"
                >
                  <FileDown className="h-4 w-4" />
                  Export
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleBulkDelete}
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedCameras([])}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={!selectedProject}
              onClick={() => setShowImportDialog(true)}
              className="gap-2"
            >
              <FileDown className="h-4 w-4" />
              Import
            </Button>
            <Button
              size="sm"
              disabled={!selectedProject}
              onClick={() => {
                setEditingCamera(null);
                setCameraForm({ name: '', camera_id: '', site_id: '', ip_address: '', location: '', description: '', rtsp_url: '', tags: [] });
                setTagInput('');
                setImageFile(null);
                setImagePreview(null);
                setShowCameraDialog(true);
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Camera
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/15 text-destructive px-4 py-3 mx-4 mt-4 rounded-lg">
          {error}
          <Button variant="ghost" size="sm" className="ml-2" onClick={() => setError('')}>×</Button>
        </div>
      )}

      {/* Cameras Grid */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading...</p>
            </div>
          </div>
        ) : !selectedProject ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center h-64">
              <FolderOpen className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Project Selected</h3>
              <p className="text-muted-foreground">Select a project from the top bar</p>
            </CardContent>
          </Card>
        ) : filteredCameras.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center h-64">
              <CameraIcon className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Cameras Found</h3>
              <p className="text-muted-foreground mb-4">Add your first camera to get started</p>
              <Button onClick={() => setShowCameraDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Camera
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredCameras.map((camera) => (
              <Card key={camera.id} className={`hover:shadow-lg transition-shadow overflow-hidden ${selectedCameras.includes(camera.id) ? 'ring-2 ring-primary' : ''}`}>
                {/* Camera Thumbnail or Placeholder */}
                <div className="w-full h-40 bg-muted overflow-hidden relative">
                  {camera.image_url ? (
                    <img
                      src={camera.image_url}
                      alt={camera.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="h-12 w-12 text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <input
                      type="checkbox"
                      checked={selectedCameras.includes(camera.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCameras([...selectedCameras, camera.id]);
                        } else {
                          setSelectedCameras(selectedCameras.filter(id => id !== camera.id));
                        }
                      }}
                      className="h-5 w-5 cursor-pointer accent-primary"
                    />
                  </div>
                </div>

                <CardContent className="p-3 space-y-2">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate flex items-center gap-1">
                        <Video className="h-3 w-3 flex-shrink-0" />
                        {camera.name}
                      </h3>
                      <p className="text-xs text-muted-foreground truncate">
                        {camera.camera_id}
                      </p>
                    </div>
                    <Badge variant={camera.status === 'active' ? 'default' : 'secondary'} className="text-xs h-5">
                      {camera.status || 'active'}
                    </Badge>
                  </div>

                  {/* Details */}
                  {camera.ip_address && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Network className="h-3 w-3" />
                      <span className="truncate font-mono">{camera.ip_address}</span>
                    </div>
                  )}
                  {camera.site_id && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Building2 className="h-3 w-3" />
                      <span className="truncate">{getSiteNameById(camera.site_id)}</span>
                    </div>
                  )}
                  {camera.location && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">{camera.location}</span>
                    </div>
                  )}

                  {/* Tags */}
                  {camera.tags && camera.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {camera.tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs h-5">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Zone Count */}
                  <div className="flex items-center gap-1 pt-1">
                    <Target className="h-3 w-3 text-primary" />
                    <span className="text-xs font-medium">
                      {zoneCounts[camera.id] || 0} zones
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 pt-1">
                    <Button
                      size="sm"
                      className="flex-1 h-8 text-xs"
                      onClick={() => handleDrawZones(camera)}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Zones
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8"
                      onClick={() => handleExportZones(camera)}
                      title="Export Zones"
                      disabled={!zoneCounts[camera.id] || zoneCounts[camera.id] === 0}
                    >
                      <FileDown className="h-3 w-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8"
                      onClick={() => openEditCamera(camera)}
                      title="Edit Camera"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => handleDeleteCamera(camera.id)}
                      title="Delete Camera"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Site Dialog */}
      <Dialog open={showSiteDialog} onOpenChange={setShowSiteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSite ? 'Edit Site' : 'Create Site'}</DialogTitle>
            <DialogDescription>
              {editingSite ? 'Update site details' : 'Add a new site to the selected project'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editingSite ? handleUpdateSite : handleCreateSite}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="site-name">Name *</Label>
                <Input
                  id="site-name"
                  value={siteForm.name}
                  onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="site-address">Address</Label>
                <Input
                  id="site-address"
                  value={siteForm.address}
                  onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="site-city">City</Label>
                  <Input
                    id="site-city"
                    value={siteForm.city}
                    onChange={(e) => setSiteForm({ ...siteForm, city: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="site-state">State</Label>
                  <Input
                    id="site-state"
                    value={siteForm.state}
                    onChange={(e) => setSiteForm({ ...siteForm, state: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="site-country">Country</Label>
                <Input
                  id="site-country"
                  value={siteForm.country}
                  onChange={(e) => setSiteForm({ ...siteForm, country: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSiteDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">{editingSite ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Camera Dialog */}
      <Dialog open={showCameraDialog} onOpenChange={setShowCameraDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingCamera ? 'Edit Camera' : 'Add Camera'}</DialogTitle>
            <DialogDescription>
              {editingCamera ? 'Update camera details' : 'Add a new camera to monitor'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editingCamera ? handleUpdateCamera : handleCreateCamera}>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label htmlFor="camera-image">Camera Image</Label>
                <div className="flex items-center gap-4">
                  {imagePreview && (
                    <div className="w-32 h-32 rounded border overflow-hidden">
                      <img
                        src={imagePreview}
                        alt="Camera preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <label
                      htmlFor="camera-image"
                      className="flex items-center justify-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-muted transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      <span className="text-sm">{imagePreview ? 'Change Image' : 'Upload Image'}</span>
                    </label>
                    <input
                      id="camera-image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>

              {/* Site Selector */}
              <div className="space-y-2">
                <Label htmlFor="camera-site">Site</Label>
                <Select
                  value={cameraForm.site_id || '__none__'}
                  onValueChange={(value) => setCameraForm({ ...cameraForm, site_id: value === '__none__' ? '' : value })}
                >
                  <SelectTrigger id="camera-site">
                    <SelectValue placeholder="No site (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No site</SelectItem>
                    {sites.filter(site => site.id && site.id !== '').map((site) => (
                      <SelectItem key={site.id} value={site.id.toString()}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="camera-name">Name *</Label>
                  <Input
                    id="camera-name"
                    value={cameraForm.name}
                    onChange={(e) => setCameraForm({ ...cameraForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="camera-id">Camera ID *</Label>
                  <Input
                    id="camera-id"
                    value={cameraForm.camera_id}
                    onChange={(e) => setCameraForm({ ...cameraForm, camera_id: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="camera-ip">IP Address (auto-extracted)</Label>
                  <Input
                    id="camera-ip"
                    value={cameraForm.ip_address}
                    onChange={(e) => setCameraForm({ ...cameraForm, ip_address: e.target.value })}
                    placeholder="Auto-filled from RTSP URL"
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">Auto-populated from RTSP URL or enter manually</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="camera-location">Location</Label>
                  <Input
                    id="camera-location"
                    value={cameraForm.location}
                    onChange={(e) => setCameraForm({ ...cameraForm, location: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="camera-rtsp">RTSP URL</Label>
                <Input
                  id="camera-rtsp"
                  value={cameraForm.rtsp_url}
                  onChange={(e) => handleRtspUrlChange(e.target.value)}
                  placeholder="rtsp://192.168.1.100:554/stream"
                />
                <p className="text-xs text-muted-foreground">IP address will be auto-extracted from RTSP URL</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="camera-description">Description</Label>
                <Input
                  id="camera-description"
                  value={cameraForm.description}
                  onChange={(e) => setCameraForm({ ...cameraForm, description: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="camera-tags">Tags</Label>
                <div className="flex gap-2">
                  <Input
                    id="camera-tags"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add tag"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddTag(e);
                      }
                    }}
                  />
                  <Button type="button" size="icon" variant="outline" onClick={handleAddTag}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {cameraForm.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {cameraForm.tags.map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCameraDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">{editingCamera ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Cameras Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Import Cameras</DialogTitle>
            <DialogDescription>
              Paste camera data below. Each line should contain: name, camera_id, rtsp_url, [ip_address], [location]
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="import-text">Camera Data (CSV format)</Label>
              <Textarea
                id="import-text"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder={`Front Door,CAM001,rtsp://192.168.1.100:554/stream1,,Main Entrance
Back Gate,CAM002,rtsp://192.168.1.101:554/stream1,192.168.1.101,Rear Exit
Parking Lot,CAM003,rtsp://admin:pass@192.168.1.102:554/live,,Parking Area`}
                className="font-mono text-xs h-64"
              />
              <p className="text-xs text-muted-foreground">
                Format: name, camera_id, rtsp_url, ip_address (optional), location (optional)
                <br />
                IP address will be auto-extracted from RTSP URL if not provided
                <br />
                Site: {selectedSiteId && selectedSiteId !== '__all__' ? getSiteNameById(selectedSiteId) : 'No site (will create without site)'}
              </p>
            </div>
            <div className="bg-muted p-3 rounded text-xs space-y-1">
              <p className="font-semibold">Example:</p>
              <p className="font-mono">Front Door,CAM001,rtsp://192.168.1.100:554/stream1,,Main Entrance</p>
              <p className="text-muted-foreground">This creates a camera named "Front Door" with ID "CAM001" at location "Main Entrance"</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleImportCameras} disabled={!importText.trim()}>
              Import Cameras
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
